import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { WEEKDAYS, type AgentSettings, type ScanRun } from "../lib/types";

const DEFAULT_COMPANY_CRITERIA = `Microsoft
Amazon
Oracle
Google
Apple
Meta
Zoom
Salesforce
Cisco
Disney
Big Tech companies in Seattle / Bellevue / Kirkland / Redmond`;

const DEFAULT_ROLE_CRITERIA = `Principal Technical Program Manager
Senior Principal Technical Program Manager
Manager Technical Program Management
Lead Technical Program Manager
Staff Technical Program Manager
Engineering Program Manager (Apple EPM equivalents)`;

export function AgentSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState<ScanRun[]>([]);

  const load = useCallback(async () => {
    if (!user) return;
    const [{ data, error: err }, runsRes] = await Promise.all([
      supabase.from("jh_agent_settings").select("*").eq("user_id", user.id).maybeSingle(),
      supabase
        .from("jh_scan_runs")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(8),
    ]);
    if (err) setError(err.message);
    else {
      const row = (data as AgentSettings | null) ?? {
        user_id: user.id,
        company_criteria: DEFAULT_COMPANY_CRITERIA,
        role_criteria: DEFAULT_ROLE_CRITERIA,
        schedule_days: ["mon", "tue", "wed", "thu", "fri"],
        email_alias: user.email ?? "",
        timezone: "America/Los_Angeles",
      };
      if (!row.company_criteria) row.company_criteria = DEFAULT_COMPANY_CRITERIA;
      if (!row.role_criteria) row.role_criteria = DEFAULT_ROLE_CRITERIA;
      setSettings(row);
    }
    if (runsRes.error) setError(runsRes.error.message);
    else setRuns((runsRes.data ?? []) as ScanRun[]);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleDay(day: string) {
    if (!settings) return;
    const has = settings.schedule_days.includes(day);
    setSettings({
      ...settings,
      schedule_days: has
        ? settings.schedule_days.filter((d) => d !== day)
        : [...settings.schedule_days, day],
    });
  }

  async function saveSettings() {
    if (!user || !settings) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.from("jh_agent_settings").upsert({
      user_id: user.id,
      company_criteria: settings.company_criteria,
      role_criteria: settings.role_criteria,
      schedule_days: settings.schedule_days,
      email_alias: settings.email_alias,
      timezone: settings.timezone,
      updated_at: new Date().toISOString(),
    });
    setBusy(false);
    if (err) setError(err.message);
    else {
      setMessage("Agent settings saved.");
      setEditing(false);
      await load();
    }
  }

  async function triggerScan() {
    if (!user) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    // Persist latest criteria before queueing
    await saveSettings();
    const { data, error: err } = await supabase
      .from("jh_scan_runs")
      .insert({
        user_id: user.id,
        trigger: "manual",
        status: "queued",
        summary: "Queued from Agent Settings",
      })
      .select("*")
      .single();
    setBusy(false);
    if (err) setError(err.message);
    else {
      setMessage(
        `Scan queued (${(data as ScanRun).id.slice(0, 8)}…). The job agent will pick this up, update your tracker, and email ${settings?.email_alias || "your alias"}.`
      );
      await load();
    }
  }

  if (!settings) return <div className="panel muted">Loading agent settings…</div>;

  return (
    <div className="stack-gap">
      <div className="panel">
        <div className="toolbar">
          <h2 style={{ margin: 0, flex: 1 }}>Agent Settings</h2>
          <button type="button" className="btn" onClick={() => setEditing((v) => !v)}>
            {editing ? "Close editor" : "Edit search agent settings"}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void triggerScan()}>
            {busy ? "Working…" : "Scan now"}
          </button>
        </div>
        <p className="muted">
          Configure company and role criteria used by the scan agent. Manual scans queue immediately;
          scheduled scans run on the selected weekdays.
        </p>
        {error ? <p className="error">{error}</p> : null}
        {message ? <p className="muted">{message}</p> : null}

        <details className="expand" open>
          <summary>Company search criteria</summary>
          <div className="body">
            <div className="field">
              <label>Companies or discovery prompts</label>
              <textarea
                disabled={!editing}
                value={settings.company_criteria}
                onChange={(e) => setSettings({ ...settings, company_criteria: e.target.value })}
              />
            </div>
          </div>
        </details>

        <details className="expand" open>
          <summary>Role / Title search criteria</summary>
          <div className="body">
            <div className="field">
              <label>Titles or role descriptions</label>
              <textarea
                disabled={!editing}
                value={settings.role_criteria}
                onChange={(e) => setSettings({ ...settings, role_criteria: e.target.value })}
              />
            </div>
          </div>
        </details>

        <div className="field">
          <label>Schedule (days the agent runs)</label>
          <div className="days">
            {WEEKDAYS.map((d) => (
              <label key={d.value}>
                <input
                  type="checkbox"
                  disabled={!editing}
                  checked={settings.schedule_days.includes(d.value)}
                  onChange={() => toggleDay(d.value)}
                />
                {d.label}
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Email alias for updates</label>
          <input
            disabled={!editing}
            type="email"
            value={settings.email_alias}
            onChange={(e) => setSettings({ ...settings, email_alias: e.target.value })}
          />
        </div>

        {editing ? (
          <button type="button" className="btn primary" disabled={busy} onClick={() => void saveSettings()}>
            Save settings
          </button>
        ) : null}
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Recent scans</h3>
        {runs.length === 0 ? (
          <p className="muted">No scans yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.created_at).toLocaleString()}</td>
                    <td>{r.trigger}</td>
                    <td>
                      <span className="pill">{r.status}</span>
                    </td>
                    <td>{r.summary || r.error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
